/**
 * 登录态上下文。
 *
 * 职责：
 *   · 应用启动时取一次 `/api/auth/state`，得到「是否已初始化 / 是否已登录 / 当前用户」；
 *   · 暴露 `refresh()` 给登录页在登录/初始化成功后重新拉取用户；
 *   · 暴露 `logout()`；
 *   · 注册全局 401 处理：任何业务接口返回 401 时把用户态清空，
 *     路由守卫据此把用户送回登录页（不做 location 硬跳转，交给 react-router）。
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  clearCsrfToken,
  fetchMe,
  getAuthState,
  logout as apiLogout,
  setUnauthorizedHandler,
} from '../api/auth'
import type { AuthUser } from '../api/auth'

export interface AuthContextValue {
  /** null = 未登录或尚未取到 */
  user: AuthUser | null
  /** 系统是否已经初始化过管理员 */
  initialized: boolean
  /** 首次加载中 */
  loading: boolean
  /** 重新拉取登录态（登录成功后调用） */
  refresh: () => Promise<void>
  /** 退出登录；成功后 user 变 null */
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [initialized, setInitialized] = useState(false)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      const state = await getAuthState()
      setInitialized(state.initialized)
      if (state.authenticated) {
        setUser(state.user)
        // /state 不返回 csrf，这里补一次 /me 把 token 拿进内存（失败不影响登录态展示）
        try {
          const me = await fetchMe()
          setUser(me.user)
        } catch {
          /* 忽略：csrf 拿不到时写操作会被服务端拒绝，下次登录即可恢复 */
        }
      } else {
        setUser(null)
        clearCsrfToken()
      }
    } catch {
      // 服务端不可达：保持未登录态，页面会提示连接失败
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiLogout()
    } catch {
      /* 即便服务端报错，本地也按已退出处理 */
    } finally {
      clearCsrfToken()
      setUser(null)
    }
  }, [])

  useEffect(() => {
    // 业务接口 401 → 清空用户态（路由守卫会自动把用户送回登录页）
    setUnauthorizedHandler(() => {
      clearCsrfToken()
      setUser(null)
    })
    return () => setUnauthorizedHandler(null)
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const value = useMemo<AuthContextValue>(
    () => ({ user, initialized, loading, refresh, logout }),
    [user, initialized, loading, refresh, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (ctx === null) {
    throw new Error('useAuth 必须在 <AuthProvider> 内使用')
  }
  return ctx
}
